import { type Currency, formatAmount } from "@loot-os/api-contract";
import type { StaffHostListEntryWire } from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import {
  AddLiveBreakHostDialog,
  AllowLiveBreakHostButton,
  RemoveLiveBreakHostButton,
} from "../components/LiveBreakHostListControls";
import { LiveBreakHostRateDialog } from "../components/LiveBreakHostRateDialog";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { ShortDate } from "../components/ShortDate";
import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import {
  LIVE_BREAK_HOST_LIST_FILTER_LABELS,
  LIVE_BREAK_HOST_LIST_FILTERS,
  type LiveBreakHostListFilter,
} from "../lib/liveBreakStaff";
import { allowerLabel, ratePercentText } from "../lib/liveBreaks";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

type HostRow = StaffHostListEntryWire;

const hostColumns = createDataTableColumns<HostRow>();
const PINNED = { start: [], end: ["actions"] };

interface ColumnArgs {
  tenantId: string;
  currency: Currency;
  canAllow: boolean;
  canSetRate: boolean;
  rates: { defaultRateBps: number; maxRateBps: number } | null;
}

function buildColumns(args: ColumnArgs) {
  const money = (value: unknown) => amountInputValue(Number(value), args.currency);
  const columns = [
    hostColumns.accessor((host) => host.username ?? "", {
      id: "username",
      header: "Username",
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
          params={{ tenantId: args.tenantId, userId: row.original.endUserId }}
          className="font-medium hover:underline"
        >
          {getValue() ? (
            `@${getValue()}`
          ) : (
            <span className="text-muted-foreground">No username</span>
          )}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    hostColumns.accessor("externalId", {
      header: "External id",
      size: 200,
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/users/$userId"
          params={{ tenantId: args.tenantId, userId: row.original.endUserId }}
          className="font-mono text-xs hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    hostColumns.accessor((host) => host.email ?? "", {
      id: "email",
      header: "Email",
      cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
      meta: { filter: "text" },
    }),
    hostColumns.accessor("effectiveRateBps", {
      header: "Rate (%)",
      size: 120,
      cell: ({ getValue, row }) => (
        <span className="flex items-center justify-end gap-2">
          {row.original.rateBps === null && (
            <span className="text-xs text-muted-foreground">default</span>
          )}
          <span className="font-mono text-xs">{ratePercentText(getValue())}</span>
        </span>
      ),
      meta: { align: "end", filter: "number", csv: (value) => ratePercentText(Number(value)) },
    }),
    hostColumns.accessor((host) => (host.allowedAt ? "Allowed" : "Not on the list"), {
      id: "allowed",
      header: "Allow List",
      cell: ({ getValue, row }) =>
        row.original.allowedAt ? (
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{getValue()}</Badge>
            <span className="text-xs text-muted-foreground">
              <ShortDate value={row.original.allowedAt} />
            </span>
          </span>
        ) : (
          <Badge variant="outline">{getValue()}</Badge>
        ),
      meta: { filter: "text" },
    }),
    hostColumns.accessor((host) => allowerLabel(host.allowedBy), {
      id: "allowedBy",
      header: "Allowed by",
      cell: ({ getValue }) => getValue() || <span className="text-muted-foreground">—</span>,
      meta: { filter: "text" },
    }),
    hostColumns.accessor("liveBreaksHosted", {
      header: "Live breaks",
      size: 112,
      meta: { align: "end", filter: "number" },
    }),
    hostColumns.accessor((host) => host.commission.unclaimedCents, {
      id: "unclaimed",
      header: amountColumnHeader("Unclaimed", args.currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), args.currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    hostColumns.accessor((host) => host.commission.claimedCents, {
      id: "claimed",
      header: amountColumnHeader("Claimed", args.currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), args.currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    hostColumns.accessor("createdAt", {
      header: "Host since",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ];
  if (!args.canAllow && !args.canSetRate) return hostColumns.columns(columns);
  return hostColumns.columns([
    ...columns,
    hostColumns.display({
      id: "actions",
      size: 180,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {args.canSetRate && args.rates && (
            <LiveBreakHostRateDialog
              host={row.original}
              defaultRateBps={args.rates.defaultRateBps}
              maxRateBps={args.rates.maxRateBps}
            />
          )}
          {args.canAllow &&
            (row.original.allowedAt ? (
              <RemoveLiveBreakHostButton host={row.original} />
            ) : (
              <AllowLiveBreakHostButton host={row.original} label="Allow" variant="ghost" />
            ))}
        </div>
      ),
    }),
  ]);
}

/**
 * Hosts (boxed's Host Allow List and host rates): every end user with a host row, their rate,
 * whether they are on the Host Allow List, the live breaks they hosted and their commission, each
 * linking to the host's page. Any member reads; editors add and remove hosts on the list, admins
 * set rates. Whether the list is the store's hosts (community live breaks off) or enforced is a
 * setting on Storefront → Live breaks.
 */
export function LiveBreakHostsPage() {
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/live-breaks/hosts" });
  const search = useSearch({ from: "/app/tenants/$tenantId/storefront/live-breaks/hosts" });
  const navigate = useNavigate({ from: "/tenants/$tenantId/storefront/live-breaks/hosts" });
  const filter: LiveBreakHostListFilter = search.filter ?? "all";
  const trpc = useTRPC();
  const currency = useCurrency();
  const { can } = useTenantRole();
  const settings = useStorefrontConfigQuery().query.data?.config.liveBreakSettings;
  const hosts = useInfiniteQuery(
    trpc.liveBreaks.hosts.list.infiniteQueryOptions(
      { filter, limit: 50 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = useMemo(() => hosts.data?.pages.flatMap((page) => page.hosts) ?? [], [hosts.data]);
  const allowedIds = useMemo(
    () => new Set(rows.filter((host) => host.allowedAt).map((host) => host.endUserId)),
    [rows],
  );
  const defaultRateBps = settings?.defaultRateBps;
  const maxRateBps = settings?.maxRateBps;
  const canAllow = can("editor");
  const canSetRate = can("admin");
  const columns = useMemo(
    () =>
      buildColumns({
        tenantId,
        currency,
        canAllow,
        canSetRate,
        rates:
          defaultRateBps === undefined || maxRateBps === undefined
            ? null
            : { defaultRateBps, maxRateBps },
      }),
    [tenantId, currency, canAllow, canSetRate, defaultRateBps, maxRateBps],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Hosts</h1>
          <p className="text-sm text-muted-foreground">
            Everyone who has hosted, been given a rate or been put on the Host Allow List, with the
            commission they have earned.{" "}
            {settings &&
              (settings.hosting === "store"
                ? "Community live breaks are off, so the list is your store's hosts: only they go live, one live break at a time."
                : settings.hostAllowlistEnforced
                  ? "The list is enforced: only the shoppers on it can start a live break."
                  : "The list isn't enforced, so anyone signed in can host.")}{" "}
            <Link
              to="/tenants/$tenantId/storefront/live-breaks"
              params={{ tenantId }}
              className="text-primary hover:underline"
            >
              Live break settings
            </Link>
          </p>
        </div>
        {canAllow && <AddLiveBreakHostDialog allowedIds={allowedIds} />}
      </div>

      {hosts.error ? (
        <ErrorAlert message={hosts.error.message} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            {/* Above the table rather than in its toolbar, whose trailing group never wraps. */}
            <div className="flex flex-wrap items-center gap-2">
              <Select
                items={LIVE_BREAK_HOST_LIST_FILTER_LABELS}
                value={filter}
                onValueChange={(value) =>
                  void navigate({
                    search: { filter: value === "all" ? undefined : (value ?? undefined) },
                    replace: true,
                  })
                }
              >
                <SelectTrigger aria-label="Which hosts" className="w-44" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIVE_BREAK_HOST_LIST_FILTERS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {LIVE_BREAK_HOST_LIST_FILTER_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hosts.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(host) => host.endUserId}
                pinned={canAllow || canSetRate ? PINNED : undefined}
                search="Search hosts"
                csvName={filter === "allowed" ? "host-allow-list" : "live-break-hosts"}
                emptyMessage={
                  filter === "allowed"
                    ? "No one is on the Host Allow List yet."
                    : "No one has hosted yet."
                }
              />
            )}
            <LoadMoreButton
              hasNextPage={hosts.hasNextPage}
              isFetchingNextPage={hosts.isFetchingNextPage}
              onClick={() => void hosts.fetchNextPage()}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
